// packages/ui-kit/src/primitives/Card.tsx
// C3F v18 — reference: design/system/components/core/Card.{jsx,prompt.md}
// Container base: 16px radius, 1px hairline, shadow only on `elevated`.
// The Paper/Night contrast between adjacent cards is the system's visual
// rhythm — one `night` card per block.
import { type HTMLAttributes, type ReactNode } from "react";

export type CardTone = "default" | "subtle" | "elevated" | "night";
type LegacyVariant = "default" | "elevated" | "soft";
type Padding = "none" | "sm" | "md" | "lg";

interface Props extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  /** @deprecated pre-v18 alias, kept for call sites: `soft` maps to `subtle`. */
  variant?: LegacyVariant;
  padding?: Padding;
  hoverable?: boolean;
  children?: ReactNode;
}

const toneCls: Record<CardTone, string> = {
  default: "bg-elevated border border-border-default",
  // Transparent border keeps every tone the same box size.
  subtle: "bg-subtle border border-transparent",
  elevated: "bg-elevated border border-border-subtle shadow-md",
  // A surface the theme does not govern carries its OWN ink: `surface-night`
  // redeclares the entire token family on the subtree, so prop-less children
  // (a ghost Button, a Badge, body text resolving --fg-default) stay legible
  // in BOTH themes with no `dark`/`onDark` props. The border alpha is the one
  // rgba the DS reference itself hardcodes for this tone.
  night: "surface-night bg-canvas border border-[rgba(244,240,233,.10)]",
};

// DS paddings: sm 14 · md 20 · lg 28.
const paddingCls: Record<Padding, string> = {
  none: "",
  sm: "p-3.5",
  md: "p-5",
  lg: "p-7",
};

export function Card({
  tone,
  variant,
  padding = "md",
  hoverable = false,
  className = "",
  children,
  ...rest
}: Props) {
  const resolvedTone: CardTone = tone ?? (variant === "soft" ? "subtle" : (variant ?? "default"));
  // The ONE sanctioned exception to "hover never darkens borders" (build.md):
  // the DS-specified card hover is border one step darker + the small shadow.
  const hoverCls = hoverable
    ? "cursor-pointer transition-[border-color,box-shadow] duration-fast hover:border-border-strong hover:shadow-sm"
    : "";
  return (
    <div
      className={`rounded-lg ${toneCls[resolvedTone]} ${paddingCls[padding]} ${hoverCls} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
